/**
 * Extract a user-facing message from API error payloads / axios errors.
 * Handles `{ success: false, message, data }` validation responses.
 */
export function getApiErrorMessage(source, fallback = "Request failed") {
  const body = source?.response?.data ?? source;
  if (body == null || body === "") {
    return source?.message || fallback;
  }
  if (typeof body === "string") {
    const trimmed = body.trim();
    return trimmed.length > 400 ? `${trimmed.slice(0, 400)}…` : trimmed;
  }

  if (typeof body.data === "string" && body.data.trim()) {
    return body.data.replace(/"/g, "").trim();
  }

  if (Array.isArray(body.data)) {
    const joined = body.data
      .map((item) => (typeof item === "string" ? item : item?.message || item?.msg || JSON.stringify(item)))
      .filter(Boolean)
      .join("; ");
    if (joined) return joined.replace(/"/g, "").trim();
  }

  if (body.data && typeof body.data === "object" && !Array.isArray(body.data)) {
    const joined = Object.entries(body.data)
      .map(([key, value]) => {
        if (Array.isArray(value)) return `${key}: ${value.join(", ")}`;
        if (value != null && value !== "") return `${key}: ${value}`;
        return null;
      })
      .filter(Boolean)
      .join("; ");
    if (joined) return joined;
  }

  if (Array.isArray(body.errors)) {
    const joined = body.errors
      .map((item) => (typeof item === "string" ? item : item?.msg || item?.message || JSON.stringify(item)))
      .filter(Boolean)
      .join("; ");
    if (joined) return joined.replace(/"/g, "").trim();
  }

  if (typeof body.message === "string" && body.message.trim()) return body.message.trim();
  if (typeof body.error === "string" && body.error.trim()) return body.error.trim();
  if (body.error?.message) return String(body.error.message);

  return source?.message || fallback;
}

export function assertApiSuccess(response, fallback = "Request failed") {
  const body = response?.data;
  if (body?.success === false) {
    throw new Error(getApiErrorMessage(body, fallback));
  }
  return response;
}
